import type { ReactNode } from "react";

type ArcGaugeProps = {
  /** 0..100. */
  readonly percent: number;
  readonly colorHex: string;
  readonly label: string;
  readonly children: ReactNode;
};

const SIZE = 220;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;
/** The arc opens at the bottom: 270 degrees, from bottom left round to bottom right. */
const SWEEP_DEGREES = 270;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC_LENGTH = (CIRCUMFERENCE * SWEEP_DEGREES) / 360;
const START_ROTATION_DEGREES = 135;

/** One big progress arc, Delta-style, with whatever sits in its middle. */
export const ArcGauge = ({ percent, colorHex, label, children }: ArcGaugeProps) => {
  const clamped = Math.min(100, Math.max(0, percent));
  const filled = (ARC_LENGTH * clamped) / 100;
  const center = SIZE / 2;
  const arc = {
    cx: center,
    cy: center,
    r: RADIUS,
    fill: "none",
    strokeWidth: STROKE,
    strokeLinecap: "round" as const,
    transform: `rotate(${START_ROTATION_DEGREES} ${center} ${center})`,
  };

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[220px]">
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="absolute inset-0 h-full w-full"
      >
        <circle
          {...arc}
          className="stroke-light-grey"
          strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE}`}
        />
        {clamped > 0 && (
          <circle {...arc} stroke={colorHex} strokeDasharray={`${filled} ${CIRCUMFERENCE}`} />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pb-3 text-center">
        {children}
      </div>
    </div>
  );
};
